import { Component, OnInit, inject, OnDestroy, HostListener, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilityService } from '../../services/client-capability.service';
import { ModalService } from '../../services/modal.service';

interface IconConfig {
  showStatsDashboard: boolean;
  showOperationalHealth: boolean;
  showEvaluation: boolean;
  showAdmin: boolean;
  showRoleBadge: boolean;
  showLoginLogout: boolean;
}

@Component({
  selector: 'app-page-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss']
})
export class PageHeaderComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  protected authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();
  private modalService = inject(ModalService);
  private clientCapability = inject(ClientCapabilityService);

  protected iconConfig: IconConfig = {
    showStatsDashboard: false,
    showOperationalHealth: false,
    showEvaluation: false,
    showAdmin: false,
    showRoleBadge: false,
    showLoginLogout: false
  };

  // Client selector properties
  protected assignedClients: Array<{client_id: string; client_name: string}> = [];
  protected currentClient: {client_id: string; client_name: string} | null = null;
  protected showClientDropdown = false;
  protected hasMultipleClients = false;
  protected isLoadingClients = true;
  protected dropdownWidth: string = '12rem'; // Adaptive width based on client name length
  protected shouldShowClientFilter = true;

  /**
   * Format client name: replace underscores with spaces and capitalize first letter of each word.
   */
  protected formatClientName(name: string): string {
    if (!name) return name;
    // Replace underscores with spaces, then capitalize first letter of each word
    return name
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  ngOnInit(): void {
    this.updateIconConfigBasedOnRoute();
    this.subscribeToClientChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToClientChanges(): void {
    // Subscribe to assigned clients
    this.authService.assignedClients$
      .pipe(takeUntil(this.destroy$))
      .subscribe((clients) => {
        this.assignedClients = clients;
        this.hasMultipleClients = clients.length > 1;
        this.calculateAdaptiveDropdownWidth();
      });

    // Subscribe to current client
    this.authService.currentClient$
      .pipe(takeUntil(this.destroy$))
      .subscribe((client) => {
        this.currentClient = client;
      });

    // Subscribe to client loading state so the header can show a loader
    // instead of "No Clients" while the assigned client is still being fetched
    this.authService.isLoadingClients$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading) => {
        this.isLoadingClients = isLoading;
      });
  }

  protected toggleClientDropdown(): void {
    if (this.hasMultipleClients) {
      this.showClientDropdown = !this.showClientDropdown;
      this.cdr.markForCheck();
    }
  }

  protected selectClient(clientId: string, event?: Event): void {
    // Stop event propagation to prevent interference from parent components
    if (event) {
      event.stopPropagation();
    }
    this.authService.setCurrentClient(clientId);
    this.showClientDropdown = false;
    // Ensure change detection runs on parent components with OnPush strategy
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Close dropdown when clicking outside
    // The dropdown has stopPropagation, so clicks inside won't reach here
    if (this.showClientDropdown) {
      this.showClientDropdown = false;
    }
  }

  private calculateAdaptiveDropdownWidth(): void {
    if (this.assignedClients.length === 0) {
      this.dropdownWidth = '12rem';
      return;
    }

    // Find the longest client name
    const longest = this.assignedClients.reduce((max, client) => 
      client.client_name.length > max.client_name.length ? client : max
    );

    // Estimate width: ~8.5 pixels per character at 0.875rem (14px) font size
    // Add padding (2rem = 32px) and apply min/max constraints
    const charWidth = 8.5;
    const padding = 32; // padding left/right (1rem each)
    const estimatedWidth = Math.ceil(longest.client_name.length * charWidth) + padding;
    
    // Constraints
    const minWidth = 192; // 12rem
    const maxWidth = 320; // 20rem
    
    const finalWidth = Math.max(minWidth, Math.min(estimatedWidth, maxWidth));
    this.dropdownWidth = `${finalWidth}px`;
  }
 
  private updateIconConfigBasedOnRoute(): void {
    const url = this.router.url;

    // Default: show client filter
    this.shouldShowClientFilter = true;

    // Login page - hide all buttons including login
    if (url.includes('/login')) {
      this.iconConfig = {
        showStatsDashboard: false,
        showOperationalHealth: false,
        showEvaluation: false,
        showAdmin: false,
        showRoleBadge: false,
        showLoginLogout: false
      };
    }
    // Home page - no admin, stats, or operational health icons
    else if (url.includes('/home')) {
      this.iconConfig = {
        showStatsDashboard: false,
        showOperationalHealth: false,
        showEvaluation: false,
        showAdmin: false,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
    // Stats Dashboard page
    else if (url.includes('/stats-dashboard')) {
      this.iconConfig = {
        showStatsDashboard: false,
        showOperationalHealth: true,
        showEvaluation: true,
        showAdmin: true,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
    // Evaluation page (loan-validation/dashboard)
    else if (url.includes('/dashboard') || url.includes('/loan-validation')) {
      this.iconConfig = {
        showStatsDashboard: true,
        showOperationalHealth: true,
        showEvaluation: false,
        showAdmin: true,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
    // Truth Management page
    else if (url.includes('/truth-management')) {
      this.iconConfig = {
        showStatsDashboard: true,
        showOperationalHealth: true,
        showEvaluation: false,
        showAdmin: true,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
    // Prompt Tuning page
    else if (url.includes('/prompt-tuning')) {
      this.iconConfig = {
        showStatsDashboard: true,
        showOperationalHealth: true,
        showEvaluation: false,
        showAdmin: true,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
    // Operational Health page
    else if (url.includes('/operations-request-analytics')) {
      this.iconConfig = {
        showStatsDashboard: true,
        showOperationalHealth: false,
        showEvaluation: true,
        showAdmin: true,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
    // Extraction Reports page - hide client filter
    else if (url.includes('/extraction-reports')) {
      this.iconConfig = {
        showStatsDashboard: true,
        showOperationalHealth: false,
        showEvaluation: true,
        showAdmin: true,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
    // Admin Settings/Training page
    else if (url.includes('/admin-settings')) {
      this.iconConfig = {
        showStatsDashboard: true,
        showOperationalHealth: true,
        showEvaluation: true,
        showAdmin: false,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
    // Edit Document Model page - hide client filter
    else if (url.includes('/edit-document-model')) {
      this.iconConfig = {
        showStatsDashboard: true,
        showOperationalHealth: true,
        showEvaluation: true,
        showAdmin: false,
        showRoleBadge: true,
        showLoginLogout: true
      };
      this.shouldShowClientFilter = false;
    }
    // Data Extraction page
    else if (url.includes('/data-extraction') && !url.includes('/data-extraction-result')) {
      this.iconConfig = {
        showStatsDashboard: true,
        showOperationalHealth: true,
        showEvaluation: false,
        showAdmin: true,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
    // Data Extraction Result page
    else if (url.includes('/data-extraction-result')) {
      this.iconConfig = {
        showStatsDashboard: true,
        showOperationalHealth: true,
        showEvaluation: false,
        showAdmin: true,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
    // Indexing / Source pages
    else if (url.includes('/indexing') || url.includes('/source')) {
      this.iconConfig = {
        showStatsDashboard: true,
        showOperationalHealth: false,
        showEvaluation: true,
        showAdmin: true,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
    // Default for other pages
    else {
      this.iconConfig = {
        showStatsDashboard: true,
        showOperationalHealth: true,
        showEvaluation: false,
        showAdmin: true,
        showRoleBadge: true,
        showLoginLogout: true
      };
    }
  }

  protected navigateToHome(): void {
    this.router.navigate(['/home']);
  }

  protected navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  protected navigateToOperationsRequestAnalytics(): void {
    this.clientCapability.navigateToOpsHealth();
  }

  protected navigateToEvaluationDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  protected navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  protected logout(): void {
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
      this.router.navigate(['/login']);
    });
  }

  protected initiateLogin(): void {
    this.authService.login();
  }
}
